// SA POV — SA creates a new client and their facilities/equipment
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TextField } from '@/shared/components/TextField';
import { ProfilePictureUpload } from '@/shared/components/ProfilePictureUpload';
import FileUpload from '@/shared/components/FileUpload';
import { FileType } from '@/shared/components/FileUpload/constants';
import { Button } from '@/shared/components/Buttons';
import { Snackbar } from '@/shared/components/Snackbar';
import AddUserModal from '@/shared/layout/createLayout/CreateModal';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { AddFacilityForm } from '@/epics/facilityManagement/components/AddFacilityForm';
import { FacilityList } from '@/epics/facilityManagement/components/FacilityList';
import { FACILITY_LABELS } from '@/epics/facilityManagement/constants/facilityLabels';
import { useAddFacility } from '@/epics/facilityManagement/hooks/useAddFacility';
import { listFacilities } from '@/epics/facilityManagement/api/listFacilityApiService';
import type { FacilityListItem } from '@/epics/facilityManagement/types/facilityListType';
import { useAddClient } from '../../hooks/useAddClient';
import { CLIENT_LABELS } from '../../constants/labels';
import { PlusIcon } from '@heroicons/react/24/outline';
import Loader from '@/app/routes/Loader';
import { UpdateStatusDialog } from '@/epics/adminManagment/pages/adminList/UpdateStatusDialog';
import { getFacilityStatusDialogConfig } from '@/epics/facilityManagement/components/FacilityList/facilityStatusDialogConfig';
import { updateFacilityStatusApiService } from '@/epics/facilityManagement/api/updateFacilityStatusApiService';
import { FACILITY_CREATE_ERROR_MESSAGES } from '@/epics/facilityManagement/errors/errorMessages';
import { Tooltip } from '@/shared/components/Tooltip';
import StepperPageShell from '@/shared/layout/StepperPageShell';
import FacilityListErrorBlock from '@/epics/facilityManagement/components/FacilityListErrorBlock';

// ─── Step tab nav ────────────────────────────────────────────────────────────

type StepTabNavProps = {
  currentStep: 1 | 2;
  onStepClick: (step: 1 | 2) => void;
  step2Accessible?: boolean;
};

const STEPS: { num: 1 | 2; label: string }[] = [
  { num: 1, label: CLIENT_LABELS.STEP1_LABEL },
  { num: 2, label: CLIENT_LABELS.STEP2_LABEL },
];

const StepTabNav = ({
  currentStep,
  onStepClick,
  step2Accessible,
}: StepTabNavProps) => (
  <nav className="flex flex-row w-full gap-0">
    {STEPS.map(({ num, label }, index) => {
      const isActive = num === currentStep;
      const isCompleted = num < currentStep;
      const isAccessible = num <= currentStep || (num === 2 && step2Accessible);
      const isLast = index === STEPS.length - 1;

      const textClass = isActive
        ? 'text-secondary-color-40'
        : isCompleted
          ? 'text-success-50'
          : 'text-neutral-60';

      const labelColor = isActive || isCompleted ? '#181A1B' : undefined;

      const borderBottomClass = isActive
        ? 'border-secondary-color-40'
        : isCompleted
          ? 'border-success-50'
          : 'border-[#DCDDE0]';

      return (
        <div
          key={num}
          className={`flex flex-col flex-1 border-b-2 ${borderBottomClass} ${
            !isLast ? 'border-r-2 border-r-[#DCDDE0]' : ''
          }`}
        >
          <button
            type="button"
            onClick={() => isAccessible && onStepClick(num)}
            className={`w-full font-medium transition-colors rounded-lg ${
              isAccessible ? 'cursor-pointer' : 'cursor-not-allowed'
            } ${textClass}`}
          >
            <span className="flex items-center justify-start gap-4">
              <span className="font-semibold text-[24px] ml-3">
                {String(num).padStart(2, '0')}
              </span>
              <span
                className="text-[14px] flex-1 text-left"
                style={labelColor ? { color: labelColor } : undefined}
              >
                {label}
              </span>
              {isCompleted && (
                <div className="mr-4">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 13L9 17L19 7"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </span>
          </button>
        </div>
      );
    })}
  </nav>
);

// ─── Currency options ────────────────────────────────────────────────────────

const CURRENCY_OPTIONS = [
  { id: 'PLN', label: 'zł – Złoty polski (PLN)' },
  { id: 'EUR', label: '€ – Euro (EUR)' },
  { id: 'USD', label: '$ – Dolar amerykański (USD)' },
];

const PAYMENT_STATUS_OPTIONS = [
  { id: 'unpaid', label: 'Oczekuje' },
  { id: 'paid', label: 'Opłacone' },
];

// ─── Step 1 Form ─────────────────────────────────────────────────────────────

const CLIENT_FIELD_ORDER = [
  'name',
  'email',
  'phone',
  'address1',
  'city',
  'state',
  'zip',
  'billing_company_name',
  'billing_nip',
  'billing_regon',
  'billing_krs',
  'invoice_address_line_1',
  'invoice_zip_code',
  'invoice_city',
  'nip',
  'contractFiles',
  'subscriptionPlan',
  'paymentStatus',
];

const AddClient = () => {
  const {
    step,
    clientId,
    formData,
    errors,
    isSubmitting,
    snackbarError,
    isSnackbarOpen,
    isCancelModalOpen,
    handleNameChange,
    handleEmailChange,
    handlePhoneChange,
    handleAddress1Change,
    handleAddress2Change,
    handleCityChange,
    handleStateChange,
    handleZipChange,
    handleNipChange,
    handleBillingCompanyNameChange,
    handleBillingNipChange,
    handleBillingRegonChange,
    handleBillingKrsChange,
    handleInvoiceAddressLine1Change,
    handleInvoiceAddressLine2Change,
    handleInvoiceZipCodeChange,
    handleInvoiceCityChange,
    handleInvoiceProvinceChange,
    handleSubscriptionPlanSelect,
    selectedPlanLabel,
    handlePaymentStatusSelect,
    handleProfileFileChange,
    handleProfileFileDelete,
    handleProfileFileError,
    handleContractFilesError,
    handleContractFilesSelect,
    contractUploadProgress,
    contractFailedFiles,
    retryContractUpload,
    handleSubmit: _handleStep1Submit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    handleBackToStep1,
    availablePlans,
    isLoadingPlans,
    hasMorePlans,
    fetchMorePlans,
    handlePlanSearchChange,
    isPlanSearchActive,
    planSearchQuery,
    isSearchLoading,
    showList,
    setShowList,
    isFinalizing,
    handleStep2Finalize,
    handleForwardToStep2,
    isAddClientEnabled,
    persistedContractDocs,
    handleExistingContractDelete,
    profileDisplayUrl,
  } = useAddClient();

  const facilityHook = useAddFacility(clientId);

  // When navigating back from step 2 → step 1, clear any unsaved facility/equipment
  // state so the user never sees stale data that was never persisted to the API.
  const prevStepRef = useRef<1 | 2>(step);
  const resetUnsavedEntries = facilityHook.resetUnsavedEntries;
  useEffect(() => {
    if (prevStepRef.current === 2 && step === 1) {
      resetUnsavedEntries();
    }
    prevStepRef.current = step;
  }, [step, resetUnsavedEntries]);

  const [facilityListRefreshKey, setFacilityListRefreshKey] = useState(0);
  const prevSavedFacilityCountRef = useRef(facilityHook.savedFacilityCount);
  const [facilities, setFacilities] = useState<FacilityListItem[]>([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [facilitiesError, setFacilitiesError] = useState<string | null>(null);
  const [maxFacilityLimit, setMaxFacilityLimit] = useState<number | null>(null);
  const [currentFacilityCount, setCurrentFacilityCount] = useState(0);
  const [facilityTotalCount, setFacilityTotalCount] = useState(0);

  // Show the loader synchronously before the browser paints when entering step 2
  // with a valid clientId. useLayoutEffect fires before paint so there is no
  // flash of empty Step2Form between the step change and the fetch starting.
  // When clientId is empty (e.g. in tests) the condition is false and the form
  // renders immediately.
  useLayoutEffect(() => {
    if (step === 2 && !!clientId) {
      setFacilitiesLoading(true);
    }
  }, [step, clientId]);

  const [facilityStatusDialog, setFacilityStatusDialog] =
    useState<FacilityListItem | null>(null);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [facilityStatusSuccess, setFacilityStatusSuccess] = useState<
    string | null
  >(null);
  const [facilityStatusError, setFacilityStatusError] = useState<string | null>(
    null
  );

  const handleFacilityToggle = (facility: FacilityListItem) => {
    setFacilityStatusDialog(facility);
  };

  const [inlineFacilityStatusPending, setInlineFacilityStatusPending] =
    useState<{
      facilityEntryId: string;
      serverId: string;
      isActive: boolean;
      name: string;
    } | null>(null);

  const handleInlineFacilityStatusConfirm = async () => {
    if (!inlineFacilityStatusPending) {
      return;
    }
    const { facilityEntryId, serverId, isActive } = inlineFacilityStatusPending;
    setIsStatusUpdating(true);
    const result = await updateFacilityStatusApiService.updateFacilityStatus({
      facilityId: serverId,
      isActive,
    });
    setIsStatusUpdating(false);
    setInlineFacilityStatusPending(null);
    if (result.success) {
      facilityHook.setFacilityItemStatus(
        facilityEntryId,
        isActive ? 'active' : 'inactive'
      );
      setFacilityStatusSuccess(
        isActive
          ? FACILITY_LABELS.ACTIVATE_SUCCESS
          : FACILITY_LABELS.DEACTIVATE_SUCCESS
      );
    } else {
      setFacilityStatusError(
        result.error ?? 'An error occurred while updating the facility status.'
      );
    }
  };

  const handleFacilityStatusConfirm = async () => {
    if (!facilityStatusDialog) {
      return;
    }
    const isCurrentlyActive = facilityStatusDialog.status === 'active';
    setIsStatusUpdating(true);
    const result = await updateFacilityStatusApiService.updateFacilityStatus({
      facilityId: facilityStatusDialog.id,
      isActive: !isCurrentlyActive,
    });
    setIsStatusUpdating(false);
    setFacilityStatusDialog(null);
    if (result.success) {
      const newStatus = isCurrentlyActive ? 'inactive' : 'active';
      setFacilities((prev) =>
        prev.map((f) =>
          f.id === facilityStatusDialog.id ? { ...f, status: newStatus } : f
        )
      );
      setFacilityStatusSuccess(
        isCurrentlyActive
          ? FACILITY_LABELS.DEACTIVATE_SUCCESS
          : FACILITY_LABELS.ACTIVATE_SUCCESS
      );
    } else {
      setFacilityStatusError(
        result.error ?? 'An error occurred while updating the facility status.'
      );
    }
  };

  useEffect(() => {
    if (!clientId || step !== 2) {
      return;
    }
    setFacilitiesLoading(true);
    setFacilitiesError(null);
    if (facilityListRefreshKey > 0) {
      setShowList(true);
    }
    listFacilities(clientId).then((result) => {
      if (result.success && result.facilities) {
        setFacilities(result.facilities);
        setShowList(result.facilities.length > 0);
        setMaxFacilityLimit(result.maxFacilityLimit ?? null);
        setCurrentFacilityCount(result.currentFacilityCount ?? 0);
        setFacilityTotalCount(result.totalCount ?? 0);
        setFacilitiesError(null);
      } else {
        setFacilitiesError(result.error ?? null);
        setShowList(false);
      }
      setFacilitiesLoading(false);
    });
  }, [clientId, step, facilityListRefreshKey, setShowList]);

  useEffect(() => {
    if (facilityHook.savedFacilityCount > prevSavedFacilityCountRef.current) {
      prevSavedFacilityCountRef.current = facilityHook.savedFacilityCount;
      setFacilityListRefreshKey((k) => k + 1);
      setShowList(true);
    }
  }, [facilityHook.savedFacilityCount, setShowList]);

  const Step1Form = useMemo(
    () => (
      <>
        {/* Basic Info */}
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <p className="text-[18px] text-on-surface-variant mt-3">
              {CLIENT_LABELS.SECTION_BASIC_INFO}
            </p>

            <ProfilePictureUpload
              onFileChange={handleProfileFileChange}
              onDelete={handleProfileFileDelete}
              onError={handleProfileFileError}
              disabled={isSubmitting}
              profileFile={formData.profileFile}
              existingImageUrl={profileDisplayUrl || undefined}
              initialLetter={
                formData.name.trim()
                  ? formData.name
                      .trim()
                      .split(' ')
                      .slice(0, 2)
                      .map((w) => w.charAt(0).toUpperCase())
                      .join('')
                  : 'A'
              }
            />
          </div>

          {/* Row 1: Name | Email | Country Code + Phone */}
          <div className="flex gap-4">
            <div className="flex-1" data-field="client-name">
              <TextField
                fullWidth
                label={CLIENT_LABELS.FIELD_NAME}
                dataTestId={CLIENT_LABELS.FIELD_NAME}
                autoTrimMaxLength={200}
                value={formData.name}
                onChange={handleNameChange}
                error={Boolean(errors.name)}
                errorMessage={errors.name}
                disabled={isSubmitting}
                autoFocus
              />
            </div>
            <div className="flex-1" data-field="client-email">
              <TextField
                fullWidth
                label={CLIENT_LABELS.FIELD_EMAIL}
                dataTestId={CLIENT_LABELS.FIELD_EMAIL}
                autoTrimMaxLength={100}
                value={formData.email}
                onChange={handleEmailChange}
                error={Boolean(errors.email)}
                errorMessage={errors.email}
                disabled={isSubmitting}
              />
            </div>
            <div className="flex-1" data-field="client-phone">
              <TextField
                fullWidth
                label={CLIENT_LABELS.FIELD_PHONE}
                dataTestId={CLIENT_LABELS.FIELD_PHONE}
                type="tel"
                maxLength={9}
                value={formData.phone}
                onChange={handlePhoneChange}
                error={Boolean(errors.phone)}
                errorMessage={errors.phone}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Row 2: Currency at 1/3 width */}
          <div className="flex gap-4">
            <div className="flex-1">
              <TextField
                fullWidth
                label={CLIENT_LABELS.FIELD_CURRENCY}
                dataTestId={CLIENT_LABELS.FIELD_CURRENCY}
                value={
                  CURRENCY_OPTIONS.find((c) => c.id === formData.currency)
                    ?.label ?? formData.currency
                }
                disabled
              />
            </div>
            <div className="flex-[2]" />
          </div>
        </div>

        <div className="border-b-2 border-[#DCDDE0]" />

        {/* Address */}
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-4">
            <p className="text-[18px] text-on-surface-variant">
              {CLIENT_LABELS.SECTION_ADDRESS}
            </p>
            <div className="flex gap-4">
              <div className="flex-1" data-field="client-address1">
                <TextField
                  fullWidth
                  label={CLIENT_LABELS.FIELD_ADDRESS1}
                  dataTestId={CLIENT_LABELS.FIELD_ADDRESS1}
                  autoTrimMaxLength={200}
                  value={formData.address1}
                  onChange={handleAddress1Change}
                  error={Boolean(errors.address1)}
                  errorMessage={errors.address1}
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex-1" data-field="client-address2">
                <TextField
                  fullWidth
                  label={
                    <>
                      {CLIENT_LABELS.FIELD_ADDRESS2}
                      <span className="text-bodySmall font-normal">
                        {' '}
                        {CLIENT_LABELS.FIELD_OPTIONAL}
                      </span>
                    </>
                  }
                  dataTestId={CLIENT_LABELS.FIELD_ADDRESS2}
                  autoTrimMaxLength={200}
                  value={formData.address2}
                  onChange={handleAddress2Change}
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex-1" data-field="client-city">
                <TextField
                  fullWidth
                  label={CLIENT_LABELS.FIELD_CITY}
                  dataTestId={CLIENT_LABELS.FIELD_CITY}
                  autoTrimMaxLength={200}
                  value={formData.city}
                  onChange={handleCityChange}
                  error={Boolean(errors.city)}
                  errorMessage={errors.city}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-1" data-field="client-state">
              <TextField
                fullWidth
                label={CLIENT_LABELS.FIELD_STATE}
                dataTestId={CLIENT_LABELS.FIELD_STATE}
                autoTrimMaxLength={200}
                value={formData.state}
                onChange={handleStateChange}
                error={Boolean(errors.state)}
                errorMessage={errors.state}
                disabled={isSubmitting}
              />
            </div>
            <div className="flex-1" data-field="client-zip">
              <TextField
                fullWidth
                label={
                  <>
                    {CLIENT_LABELS.FIELD_ZIP}
                    <span className="text-bodySmall font-normal">
                      {' '}
                      {CLIENT_LABELS.FIELD_OPTIONAL}
                    </span>
                  </>
                }
                dataTestId={CLIENT_LABELS.FIELD_ZIP}
                maxLength={6}
                value={formData.zip}
                onChange={handleZipChange}
                error={Boolean(errors.zip)}
                errorMessage={errors.zip}
                disabled={isSubmitting}
              />
            </div>
            <div className="flex-1">
              <TextField
                fullWidth
                label={CLIENT_LABELS.FIELD_COUNTRY}
                dataTestId={CLIENT_LABELS.FIELD_COUNTRY}
                value={formData.country}
                disabled
              />
            </div>
          </div>
        </div>

        <div className="border-b-2 border-[#DCDDE0]" />

        {/* Billing Details */}
        <div className="flex flex-col gap-4">
          <p className="text-[18px] text-on-surface-variant">
            {CLIENT_LABELS.SECTION_BILLING_DETAILS}
          </p>

          {/* Company Information subsection */}
          <div>
            <p className="text-bodyLarge text-on-surface-variant mb-2">
              {CLIENT_LABELS.SECTION_BILLING_COMPANY_INFO}
            </p>
            <div className="flex flex-col gap-8">
              <div className="flex gap-4">
                <div
                  className="flex-1"
                  data-field="client-billing_company_name"
                >
                  <TextField
                    fullWidth
                    label={CLIENT_LABELS.FIELD_BILLING_COMPANY_NAME}
                    dataTestId={CLIENT_LABELS.FIELD_BILLING_COMPANY_NAME}
                    autoTrimMaxLength={200}
                    value={formData.billing_company_name}
                    onChange={handleBillingCompanyNameChange}
                    error={Boolean(errors.billing_company_name)}
                    errorMessage={errors.billing_company_name}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex-1" data-field="client-billing_nip">
                  <TextField
                    fullWidth
                    label={CLIENT_LABELS.FIELD_BILLING_NIP}
                    dataTestId="billing_nip"
                    value={formData.billing_nip}
                    onChange={handleBillingNipChange}
                    error={Boolean(errors.billing_nip)}
                    errorMessage={errors.billing_nip}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex-1" data-field="client-billing_regon">
                  <TextField
                    fullWidth
                    label={
                      <>
                        {CLIENT_LABELS.FIELD_BILLING_REGON}
                        <span className="text-bodySmall font-normal">
                          {' '}
                          {CLIENT_LABELS.FIELD_OPTIONAL}
                        </span>
                      </>
                    }
                    dataTestId={CLIENT_LABELS.FIELD_BILLING_REGON}
                    value={formData.billing_regon}
                    onChange={handleBillingRegonChange}
                    error={Boolean(errors.billing_regon)}
                    errorMessage={errors.billing_regon}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-1 min-w-0" data-field="client-billing_krs">
                  <TextField
                    fullWidth
                    label={
                      <>
                        {CLIENT_LABELS.FIELD_BILLING_KRS}
                        <span className="text-bodySmall font-normal">
                          {' '}
                          {CLIENT_LABELS.FIELD_OPTIONAL}
                        </span>
                      </>
                    }
                    dataTestId={CLIENT_LABELS.FIELD_BILLING_KRS}
                    value={formData.billing_krs}
                    onChange={handleBillingKrsChange}
                    error={Boolean(errors.billing_krs)}
                    errorMessage={errors.billing_krs}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex-1" />
                <div className="flex-1" />
              </div>
            </div>
          </div>

          {/* Invoice Address subsection */}
          <div className="flex flex-col gap-8">
            <div>
              <p className="text-bodyLarge text-on-surface-variant mb-2">
                {CLIENT_LABELS.SECTION_BILLING_INVOICE_ADDRESS}
              </p>
              <div className="flex gap-4">
                <div
                  className="flex-1"
                  data-field="client-invoice_address_line_1"
                >
                  <TextField
                    fullWidth
                    label={CLIENT_LABELS.FIELD_INVOICE_ADDRESS_LINE_1}
                    dataTestId={CLIENT_LABELS.FIELD_INVOICE_ADDRESS_LINE_1}
                    autoTrimMaxLength={200}
                    value={formData.invoice_address_line_1}
                    onChange={handleInvoiceAddressLine1Change}
                    error={Boolean(errors.invoice_address_line_1)}
                    errorMessage={errors.invoice_address_line_1}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex-1">
                  <TextField
                    fullWidth
                    label={
                      <>
                        {CLIENT_LABELS.FIELD_INVOICE_ADDRESS_LINE_2}
                        <span className="text-bodySmall font-normal">
                          {' '}
                          {CLIENT_LABELS.FIELD_OPTIONAL}
                        </span>
                      </>
                    }
                    dataTestId="invoice_address_line_2"
                    autoTrimMaxLength={200}
                    value={formData.invoice_address_line_2}
                    onChange={handleInvoiceAddressLine2Change}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex-1" data-field="client-invoice_zip_code">
                  <TextField
                    fullWidth
                    label={CLIENT_LABELS.FIELD_INVOICE_ZIP_CODE}
                    dataTestId="invoice_zip_code"
                    maxLength={6}
                    value={formData.invoice_zip_code}
                    onChange={handleInvoiceZipCodeChange}
                    error={Boolean(errors.invoice_zip_code)}
                    errorMessage={errors.invoice_zip_code}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-1 min-w-0" data-field="client-invoice_city">
                <TextField
                  fullWidth
                  label={CLIENT_LABELS.FIELD_INVOICE_CITY}
                  dataTestId="invoice_city"
                  autoTrimMaxLength={200}
                  value={formData.invoice_city}
                  onChange={handleInvoiceCityChange}
                  error={Boolean(errors.invoice_city)}
                  errorMessage={errors.invoice_city}
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex-1">
                <TextField
                  fullWidth
                  label={
                    <>
                      {CLIENT_LABELS.FIELD_INVOICE_PROVINCE}
                      <span className="text-bodySmall font-normal">
                        {' '}
                        {CLIENT_LABELS.FIELD_OPTIONAL}
                      </span>
                    </>
                  }
                  dataTestId="invoice_province"
                  autoTrimMaxLength={200}
                  value={formData.invoice_province}
                  onChange={handleInvoiceProvinceChange}
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex-1" />
            </div>
          </div>
        </div>

        <div className="border-b-2 border-[#DCDDE0]" />

        {/* Tax & Compliance */}
        <div className="flex flex-col gap-8">
          <div>
            <p className="text-[18px] text-on-surface-variant mb-4">
              {CLIENT_LABELS.SECTION_TAX_COMPLIANCE}
            </p>
            <div className="flex gap-4">
              <div className="flex-1" data-field="client-nip">
                <TextField
                  fullWidth
                  label={CLIENT_LABELS.FIELD_NIP}
                  dataTestId={CLIENT_LABELS.FIELD_NIP}
                  autoTrimMaxLength={13}
                  value={formData.nip}
                  onChange={handleNipChange}
                  error={Boolean(errors.nip)}
                  errorMessage={errors.nip}
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex-2 " />
            </div>
          </div>

          <div
            className="flex flex-col gap-2"
            data-field="client-contractFiles"
          >
            <p className="text-[16px] text-on-surface-variant">
              {CLIENT_LABELS.SECTION_CONTRACT}
            </p>
            <FileUpload
              onFileSelect={handleContractFilesSelect}
              onError={handleContractFilesError}
              selectedFiles={formData.contractFiles ?? []}
              existingFiles={persistedContractDocs.map((doc) => ({
                filename: doc.filename,
                fileSize: doc.fileSize,
                path: doc.path,
              }))}
              onExistingFileDelete={handleExistingContractDelete}
              allowedTypes={[
                FileType.PNG,
                FileType.JPG,
                FileType.JPEG,
                FileType.PDF,
              ]}
              maxFiles={2}
              disabled={isSubmitting}
              error={Boolean(errors.contractFiles)}
              errorMessage={errors.contractFiles || undefined}
              uploadProgress={contractUploadProgress}
              failedFiles={contractFailedFiles}
              onRetry={retryContractUpload}
            />
          </div>
        </div>

        <div className="border-b-2 border-[#DCDDE0]" />

        {/* Subscription Details */}
        <div className="flex flex-col gap-4">
          <p className="text-[18px] text-on-surface-variant">
            {CLIENT_LABELS.SECTION_SUBSCRIPTION}
          </p>
          <div className="flex gap-4">
            <div
              className="flex-1 min-w-0"
              data-field="client-subscriptionPlan"
            >
              <Dropdown
                menuFullWidth
                menuMaxVisibleItems={7}
                toolTip={false}
                items={
                  isSearchLoading && planSearchQuery
                    ? [
                        {
                          id: '__loading__',
                          label: CLIENT_LABELS.DROPDOWN_LOADING,
                          disabled: true,
                        },
                      ]
                    : isLoadingPlans && !planSearchQuery
                      ? [
                          ...availablePlans,
                          {
                            id: '__loading__',
                            label: CLIENT_LABELS.DROPDOWN_LOADING,
                            disabled: true,
                          },
                        ]
                      : !isSearchLoading &&
                          planSearchQuery &&
                          availablePlans.length === 0
                        ? [
                            {
                              id: '__no_results__',
                              label: 'Nie znaleziono wyników',
                              disabled: true,
                            },
                          ]
                        : availablePlans
                }
                selectedId={formData.subscriptionPlan}
                placement="top-start"
                onItemClick={(id) => {
                  if (id !== '__loading__') {
                    handleSubscriptionPlanSelect(id);
                  }
                }}
                onScrollEnd={() => {
                  if (!isPlanSearchActive && hasMorePlans && !isLoadingPlans) {
                    fetchMorePlans();
                  }
                }}
                onSearchChange={handlePlanSearchChange}
                onOpenChange={(open) => {
                  if (!open) {
                    handlePlanSearchChange('');
                  }
                }}
                disabled={isSubmitting}
                error={Boolean(errors.subscriptionPlan)}
                errorMessage={errors.subscriptionPlan}
                showSearch
                searchPlaceholder={
                  CLIENT_LABELS.SUBSCRIPTION_PLAN_SEARCH_PLACEHOLDER
                }
                trigger={({
                  open,
                  toggle,
                  disabled: d,
                  readOnly,
                  error: e,
                }) => (
                  <DropDownButton
                    label={CLIENT_LABELS.FIELD_SUBSCRIPTION_PLAN}
                    value={
                      availablePlans.find(
                        (p) => p.id === formData.subscriptionPlan
                      )?.label ?? selectedPlanLabel
                    }
                    open={open}
                    toggle={toggle}
                    disabled={d}
                    readOnly={readOnly ?? false}
                    error={e}
                  />
                )}
              />
            </div>
            <div className="flex-1" data-field="client-paymentStatus">
              <Dropdown
                menuFullWidth
                toolTip={false}
                items={PAYMENT_STATUS_OPTIONS}
                selectedId={formData.paymentStatus}
                placement="top-start"
                onItemClick={handlePaymentStatusSelect}
                disabled={isSubmitting}
                error={Boolean(errors.paymentStatus)}
                errorMessage={errors.paymentStatus}
                trigger={({
                  open,
                  toggle,
                  disabled: d,
                  readOnly,
                  error: e,
                }) => (
                  <DropDownButton
                    label={CLIENT_LABELS.FIELD_PAYMENT_STATUS}
                    value={
                      PAYMENT_STATUS_OPTIONS.find(
                        (s) => s.id === formData.paymentStatus
                      )?.label ?? ''
                    }
                    open={open}
                    toggle={toggle}
                    disabled={d}
                    readOnly={readOnly ?? false}
                    error={e}
                  />
                )}
              />
            </div>
            <div className="flex-1" />
          </div>
        </div>
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formData,
      errors,
      isSubmitting,
      isLoadingPlans,
      isSearchLoading,
      planSearchQuery,
      availablePlans,
      hasMorePlans,
      fetchMorePlans,
      handleContractFilesError,
      persistedContractDocs,
      handleExistingContractDelete,
      contractUploadProgress,
      contractFailedFiles,
      retryContractUpload,
    ]
  );

  const Step2Form = (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between mt-3 items-center">
        <p className="text-titleDefault font-normal text-on-surface-variant">
          {FACILITY_LABELS.FACILITY_TITLE} ({facilities.length})
        </p>
        {facilities.length > 0 &&
          showList &&
          (() => {
            const HARD_LIMIT = 10;
            const effectiveCount = Math.max(
              currentFacilityCount,
              facilityTotalCount
            );
            const isAtHardLimit = effectiveCount >= HARD_LIMIT;
            const isAtPlanLimit =
              !isAtHardLimit &&
              maxFacilityLimit !== null &&
              effectiveCount >= maxFacilityLimit;
            const isAddDisabled =
              isSubmitting || isFinalizing || isAtHardLimit || isAtPlanLimit;
            const tooltipContent = isAtHardLimit
              ? FACILITY_CREATE_ERROR_MESSAGES.HARD_LIMIT_TOOLTIP
              : isAtPlanLimit && maxFacilityLimit !== null
                ? FACILITY_CREATE_ERROR_MESSAGES.LIMIT_REACHED_TOOLTIP(
                    maxFacilityLimit
                  )
                : null;
            const btn = (
              <Button
                label={'Dodaj placówkę'}
                buttonType="Square"
                type="button"
                onClick={() => {
                  facilityHook.addFacilityEntry();
                  setShowList(false);
                }}
                className={`!h-[40px] border !text-labelLarge !rounded-[8px] ${
                  isAddDisabled
                    ? 'bg-gray-300 border-gray-300 text-gray-500 cursor-not-allowed'
                    : '!bg-transparent border-[#2A4455] !text-[#2A4455] hover:!border-[#2A4455] hover:!bg-[#474A4C14] cursor-pointer'
                }`}
                disabled={isAddDisabled}
                icon={
                  <PlusIcon
                    className="w-7 h-5"
                    color={isAddDisabled ? '#9CA3AF' : '#2A4455'}
                  />
                }
              />
            );
            return tooltipContent ? (
              <Tooltip
                content={tooltipContent}
                forceShow
                side="bottom"
                contentMaxWidth={280}
                triggerWrapContent
              >
                {btn}
              </Tooltip>
            ) : (
              btn
            );
          })()}
      </div>
      {facilities.length > 0 && showList && (
        <FacilityList
          facilities={facilities}
          loading={facilitiesLoading}
          error={facilitiesError}
          onToggle={handleFacilityToggle}
          onMenuAction={(action, facility) => {
            if (action === 'EDIT') {
              facilityHook.resetUnsavedEntries();
              void facilityHook.loadFacilityForEditing(facility.id);
              setShowList(false);
            }
          }}
        />
      )}

      {facilityHook.facilityList.map((facility, i, arr) => {
        const isEditMode = facilityHook.getIsEditMode(facility.id);
        const isLastItem = i === arr.length - 1;

        // Skip rendering while loading a facility to prevent flicker
        if (facilityHook.isLoading && !showList) {
          return null;
        }

        // When showing the list: don't render any forms
        if (showList) {
          return null;
        }

        // When not showing the list: only show ONE form at a time
        // Either the form being edited (isEditMode = true) OR the last form in the array (most recent add)
        if (!isEditMode && !isLastItem) {
          return null;
        }
        return (
          <AddFacilityForm
            key={facility.id}
            facility={facility}
            errors={
              facilityHook.facilityErrorsList[i] ?? {
                name: '',
                nip: '',
                phone: '',
                address1: '',
                city: '',
                state: '',
                zip: '',
              }
            }
            mode={isEditMode ? 'edit' : 'add'}
            isSubmitting={facilityHook.isSubmitting || isFinalizing}
            isFacilitySubmitting={facilityHook.isFacilitySubmitting}
            isLoading={facilityHook.loadingFacilityId === facility.id}
            {...(isEditMode &&
              (() => {
                const fStatus = facilityHook.getFacilityStatus(facility.id);
                return {
                  ...(fStatus !== undefined && { facilityStatus: fStatus }),
                  isStatusUpdating,
                  onStatusToggle: (isActive: boolean) => {
                    const serverId = facilityHook.getTempFacilityId(
                      facility.id
                    );
                    if (serverId) {
                      setInlineFacilityStatusPending({
                        facilityEntryId: facility.id,
                        serverId,
                        isActive,
                        name: facility.name,
                      });
                    }
                  },
                };
              })())}
            clientId={clientId}
            serverFacilityId={facilityHook.getTempFacilityId(facility.id)}
            equipmentRefreshKey={
              facilityHook.getEquipmentServerIds(facility.id).filter(Boolean)
                .length + facilityHook.equipmentEditCount
            }
            equipmentList={facilityHook.getEquipmentList(facility.id)}
            equipmentErrors={facilityHook.getEquipmentErrors(facility.id)}
            uploadProgressList={facilityHook.getUploadProgress(facility.id)}
            failedUploadFilesList={facilityHook.getFailedUploadFiles(
              facility.id
            )}
            equipmentServerIds={facilityHook.getEquipmentServerIds(facility.id)}
            equipmentConflicts={facilityHook.getEquipmentConflicts(facility.id)}
            onEquipmentConflictsClear={() =>
              facilityHook.clearEquipmentConflicts(facility.id)
            }
            onFacilityFieldChange={(field, value) =>
              facilityHook.updateFacilityField(facility.id, field, value)
            }
            onNipChange={(raw) =>
              facilityHook.handleFacilityNipChange(facility.id, raw)
            }
            onPhoneChange={(raw) =>
              facilityHook.handleFacilityPhoneChange(facility.id, raw)
            }
            onAddEquipment={() => facilityHook.addEquipmentEntry(facility.id)}
            onEquipmentFieldChange={(equipId, field, value) =>
              facilityHook.updateEquipmentField(
                facility.id,
                equipId,
                field,
                value
              )
            }
            onEquipmentCategoryChange={(equipId, value) =>
              facilityHook.updateEquipmentCategory(facility.id, equipId, value)
            }
            onEquipmentDateOfProductionChange={(equipId, date) =>
              facilityHook.updateEquipmentDateOfProduction(
                facility.id,
                equipId,
                date
              )
            }
            onEquipmentImageChange={(equipId, files) =>
              facilityHook.handleEquipmentImageChange(
                facility.id,
                equipId,
                files
              )
            }
            onEquipmentImageError={(equipId, msg) =>
              facilityHook.handleEquipmentImageError(facility.id, equipId, msg)
            }
            onEquipmentDocsChange={(equipId, files) =>
              facilityHook.handleEquipmentDocsChange(
                facility.id,
                equipId,
                files
              )
            }
            onEquipmentDocsError={(equipId, msg) =>
              facilityHook.handleEquipmentDocsError(facility.id, equipId, msg)
            }
            onEquipmentRetry={(equipId, filename) =>
              void facilityHook.retryEquipmentUpload(
                facility.id,
                equipId,
                filename
              )
            }
            onEquipmentRemove={(equipId) =>
              facilityHook.removeEquipmentEntry(facility.id, equipId)
            }
            onEquipmentReset={(equipId) =>
              facilityHook.resetEquipmentEntry(facility.id, equipId)
            }
            onEquipmentValidate={(equipId) => {
              void facilityHook.submitEquipmentEntry(
                clientId,
                facility.id,
                equipId
              );
            }}
            onEditEquipment={(item) =>
              facilityHook.startEquipmentEdit(facility.id, item)
            }
            editingEquipment={facilityHook.getEditingEquipment(facility.id)}
            onEquipmentEditFieldChange={(field, value) =>
              facilityHook.updateEditEquipmentField(facility.id, field, value)
            }
            onEquipmentEditCategoryChange={(value) =>
              facilityHook.updateEditEquipmentCategory(facility.id, value)
            }
            onEquipmentEditDateOfProductionChange={(date) =>
              facilityHook.updateEditEquipmentDateOfProduction(
                facility.id,
                date
              )
            }
            onEquipmentEditImageChange={(files) =>
              facilityHook.handleEditEquipmentImageChange(facility.id, files)
            }
            onEquipmentEditImageError={(msg) =>
              facilityHook.handleEditEquipmentImageError(facility.id, msg)
            }
            onEquipmentEditDocsChange={(files) =>
              facilityHook.handleEditEquipmentDocsChange(facility.id, files)
            }
            onEquipmentEditDocsError={(msg) =>
              facilityHook.handleEditEquipmentDocsError(facility.id, msg)
            }
            onEquipmentEditExistingImageDelete={() =>
              facilityHook.handleEditExistingImageDelete(facility.id)
            }
            onEquipmentEditExistingDocDelete={(path) =>
              facilityHook.handleEditExistingDocDelete(facility.id, path)
            }
            onEquipmentEditRetry={(filename) =>
              void facilityHook.retryUploadSingleFile(facility.id, filename)
            }
            onEquipmentEditSubmit={() => {
              void facilityHook.submitEquipmentEdit(facility.id);
            }}
            onEquipmentEditCancel={() =>
              facilityHook.cancelEquipmentEdit(facility.id)
            }
            onSubmit={() => {
              void facilityHook.submitFacilityOnly(clientId, facility.id);
            }}
            onCancel={() => {
              facilityHook.exitEditMode(facility.id);
              setShowList(true);
            }}
            onClearFacility={() =>
              facilityHook.resetFacilityFields(facility.id)
            }
          />
        );
      })}
    </div>
  );

  const isAnyFacilityFormDirty = facilityHook.facilityList.some(
    (f) =>
      f.name !== '' ||
      f.nip !== '' ||
      f.phone !== '' ||
      f.address1 !== '' ||
      f.address2 !== '' ||
      f.city !== '' ||
      f.state !== '' ||
      f.zip !== ''
  );

  const isAnyEquipmentFormDirty = facilityHook.facilityList.some((f) => {
    const equipList = facilityHook.getEquipmentList(f.id);
    const serverIds = facilityHook.getEquipmentServerIds(f.id);
    return equipList.some(
      (e, i) =>
        !serverIds[i] &&
        (e.name !== '' ||
          e.description !== '' ||
          e.category !== '' ||
          e.serialNumber !== '' ||
          e.modelName !== '' ||
          e.dateOfProduction !== undefined ||
          e.imageFile !== undefined ||
          e.docFiles.length > 0)
    );
  });

  const submitRequestedRef = useRef(false);

  useEffect(() => {
    if (!submitRequestedRef.current) {
      return;
    }
    submitRequestedRef.current = false;
    const firstErrorField = CLIENT_FIELD_ORDER.find((f) =>
      Boolean((errors as Record<string, string>)[f])
    );
    if (!firstErrorField) {
      return;
    }
    document
      .querySelector(`[data-field="client-${firstErrorField}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [errors]);

  const handleStep1FormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submitRequestedRef.current = true;
    void handleForwardToStep2();
  };

  const handleStep2FormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void handleStep2Finalize();
  };

  const handleSubmit =
    step === 1 ? handleStep1FormSubmit : handleStep2FormSubmit;

  const submitLabel =
    step === 2 && isFinalizing
      ? CLIENT_LABELS.SUBMIT_BUTTON_LOADING
      : step === 1
        ? CLIENT_LABELS.SUBMIT_BUTTON
        : CLIENT_LABELS.SUBMIT_BUTTON_STEP2;

  return (
    <>
      <StepperPageShell
        as="form"
        onSubmit={handleSubmit}
        title={CLIENT_LABELS.PAGE_TITLE}
        stepper={
          <StepTabNav
            currentStep={step}
            step2Accessible={isAddClientEnabled}
            onStepClick={(s) => {
              if (s === 1) {
                handleBackToStep1();
              } else if (s === 2) {
                handleForwardToStep2();
              }
            }}
          />
        }
        headerClassName="gap-5.5"
        footerClassName=""
        footer={
          <>
            <Button
              label={CLIENT_LABELS.CANCEL_BUTTON}
              buttonType="Square"
              type="button"
              onClick={handleCancel}
              className="!bg-transparent !h-[40px] cursor-pointer !text-labelLarge !text-on-surface-variant hover:!border-[#DCDDE0] hover:!bg-[#474A4C]/[0.08] !rounded-[8px]"
              disabled={
                isSubmitting || isFinalizing || facilityHook.isSubmitting
              }
            />
            <div className="flex gap-4">
              {step === 2 && (
                <Button
                  label={CLIENT_LABELS.BACK_BUTTON}
                  buttonType="Square"
                  type="button"
                  onClick={handleBackToStep1}
                  disabled={
                    isSubmitting || isFinalizing || facilityHook.isSubmitting
                  }
                  className="!bg-transparent !h-[40px] cursor-pointer border border-[#DCDDE0] !text-labelLarge !text-on-surface-variant hover:!border-[#DCDDE0] hover:!bg-[#474A4C]/[0.08] !rounded-[8px]"
                />
              )}
              <Button
                label={submitLabel}
                type="submit"
                disabled={
                  step === 1
                    ? isSubmitting ||
                      Object.keys(contractUploadProgress).length > 0
                    : isFinalizing ||
                      !isAddClientEnabled ||
                      facilities.length === 0 ||
                      isAnyFacilityFormDirty ||
                      isAnyEquipmentFormDirty
                }
                className="!h-[40px] text-labelLarge cursor-pointer !rounded-[8px]"
              />
            </div>
          </>
        }
      >
        {step === 1 ? (
          Step1Form
        ) : facilitiesLoading ? (
          <div className="flex items-center justify-center w-full h-full min-h-[300px]">
            <Loader />
          </div>
        ) : facilitiesError ? (
          <FacilityListErrorBlock
            onRetry={() => setFacilityListRefreshKey((k) => k + 1)}
          />
        ) : (
          Step2Form
        )}
      </StepperPageShell>

      <Snackbar
        open={isSnackbarOpen}
        message={snackbarError}
        variant="error"
        onClose={handleCloseSnackbar}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={facilityHook.isSnackbarOpen}
        message={facilityHook.snackbarError}
        variant="error"
        onClose={facilityHook.handleCloseSnackbar}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={facilityHook.isSuccessSnackbarOpen}
        message={FACILITY_LABELS.ADD_FACILITY_SUCCESS}
        variant="success"
        onClose={facilityHook.handleCloseSuccessSnackbar}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={facilityHook.isEquipmentSuccessSnackbarOpen}
        message={FACILITY_LABELS.ADD_EQUIPMENT_SUCCESS}
        variant="success"
        onClose={facilityHook.handleCloseEquipmentSuccessSnackbar}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={facilityHook.isEquipmentEditSuccessSnackbarOpen}
        message={FACILITY_LABELS.EDIT_EQUIPMENT_SUCCESS}
        variant="success"
        onClose={facilityHook.handleCloseEquipmentEditSuccessSnackbar}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <AddUserModal
        open={isCancelModalOpen}
        onClose={handleCloseCancelModal}
        onConfirmCancel={handleConfirmCancel}
      />

      {facilityStatusDialog !== null && (
        <UpdateStatusDialog
          open
          onClose={() => {
            if (!isStatusUpdating) {
              setFacilityStatusDialog(null);
            }
          }}
          onConfirm={() => {
            void handleFacilityStatusConfirm();
          }}
          isSubmitting={isStatusUpdating}
          {...getFacilityStatusDialogConfig(
            facilityStatusDialog.name,
            facilityStatusDialog.status === 'active'
          )}
        />
      )}

      {/* Inline facility edit status dialog */}
      {inlineFacilityStatusPending !== null && (
        <UpdateStatusDialog
          open
          onClose={() => {
            if (!isStatusUpdating) {
              setInlineFacilityStatusPending(null);
            }
          }}
          onConfirm={() => {
            void handleInlineFacilityStatusConfirm();
          }}
          isSubmitting={isStatusUpdating}
          {...getFacilityStatusDialogConfig(
            inlineFacilityStatusPending.name,
            !inlineFacilityStatusPending.isActive
          )}
        />
      )}

      <Snackbar
        open={facilityStatusSuccess !== null}
        message={facilityStatusSuccess ?? ''}
        variant="success"
        onClose={() => setFacilityStatusSuccess(null)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={facilityStatusError !== null}
        message={facilityStatusError ?? ''}
        variant="error"
        onClose={() => setFacilityStatusError(null)}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton
      />
    </>
  );
};

export default AddClient;
